/**
 * Предполётная сверка биржевой ноги: почему на эту строку деньги не пойдут.
 *
 * Порог здесь выше, чем у ленты, и это намеренно. Открыть вкладку конторы по
 * сомнительной строке стоит человеку двадцать секунд и ничего больше — он сам
 * увидит там настоящую цену и закроет вкладку. Нажатие «купить» на бирже стоит
 * денег сразу: ордер уходит, шары покупаются, и обнаружить, что второй ноги
 * никогда не было, можно только постфактум — уже держа голую позицию.
 *
 * Поэтому здесь отказ по подозрению, а не только по доказанному. Каждый повод
 * ниже — это случай, когда числа на экране могут не соответствовать рынку, а
 * потолок цены (`poly/order.ts`) считается ровно от этих чисел: ошиблись в
 * кэфе второй ноги — ошиблись в потолке, и ордер уйдёт по цене, при которой
 * вилки уже нет.
 *
 * Чего здесь НЕТ и почему.
 *
 * Цены самой биржи в этом списке не проверяются: её называет сама биржа перед
 * подписью, и сверка с потолком делается там (`checkQuote`). Дублировать её
 * догадкой из ленты значило бы отказывать по устаревшему числу в тот момент,
 * когда точное уже доступно.
 *
 * Залипшая биржевая нога тоже не повод: покой цены в стакане тот же `checkQuote`
 * поймает точнее. А вот залипшая нога КОНТОРЫ — повод, и главный: именно её кэф
 * задаёт потолок, проверить его нам нечем, и «цена не двигалась две с половиной
 * минуты» в живой линии означает, что за ней никто не следит.
 */

import type { RowLeg } from '../lib/row';
import type { FeedRow } from './rows';

export interface Sight {
  /** Строка заморожена руками: числа на экране намеренно не обновляются. */
  frozen: boolean;
  /** Деньги пересчитываются: суммы ног посчитаны по прежнему банку или курсу. */
  stale: boolean;
}

export interface Refusal {
  ok: boolean;
  why: string;
}

const FINE: Refusal = { ok: true, why: '' };

/**
 * Пускать ли ордер по этой строке.
 *
 * Возвращает первую причину отказа, а не список: человек читает одну строку под
 * кнопкой, и «причин пять» он всё равно прочтёт как «нельзя».
 */
export function preflight(row: FeedRow, other: RowLeg, sight: Sight): Refusal {
  if (row.ghost !== null) {
    return { ok: false, why: 'вилки уже нет в ленте, на экране остался её след' };
  }
  if (row.row.doubt === 'dead') {
    return { ok: false, why: 'лента считает цену неверной' };
  }
  if (sight.frozen) {
    return { ok: false, why: 'лента заморожена, числа на экране не обновляются' };
  }
  if (sight.stale) {
    return { ok: false, why: 'суммы пересчитываются под новый банк' };
  }
  const still = row.still;
  if (still !== null && still.source === other.source) {
    return { ok: false, why: `кэф ${still.source} не двигался, потолок считать не от чего` };
  }
  return FINE;
}
